import {
    createContext,
    useContext,
    useEffect,
    useReducer,
    type ReactNode,
} from 'react';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { Location, Repairer } from '@/types/types';

export type ServiceMediaType = 'Image' | 'Video' | 'PDF' | 'Text';

export interface ServiceMedia {
    id: string;
    name: string;
    media_type: ServiceMediaType;
    dataUrl?: string;
    text?: string;
}

export interface NewJobServiceState {
    id: number;
    type: string;
    repair: string;
    parts: string[];
    position: string;
    price: string;
    prices: string[];
    priceEach: boolean;
    currency: string;
    totalDP: string;
    repairer: { id?: string; name?: string };
    media: ServiceMedia[];
}

const INITIAL_SERVICE_STATE: NewJobServiceState = {
    id: 0,
    type: '',
    repair: '',
    parts: [],
    position: '',
    price: '0',
    prices: ['0'],
    priceEach: false,
    currency: 'USD',
    totalDP: '$0.00',
    repairer: {},
    media: [],
};

export interface NewJobState {
    location: Location | null;
    supplier: Repairer | null;
    vin: string;
    color: string;
    odometer: string;
    stockRO: string;
    poNumber: string;
    services: NewJobServiceState[];
    service: NewJobServiceState;
    editID: number;
}

const INITIAL_STATE: NewJobState = {
    location: null,
    supplier: null,
    vin: '',
    color: '',
    odometer: '',
    stockRO: '',
    poNumber: '',
    services: [],
    service: INITIAL_SERVICE_STATE,
    editID: 0,
};

export type NewJobAction =
    | { type: 'SET_LOCATION'; payload: Location | null }
    | { type: 'SET_SUPPLIER'; payload: Repairer | null }
    | { type: 'SET_VIN'; payload: string }
    | { type: 'SET_COLOR'; payload: string }
    | { type: 'SET_ODO'; payload: string }
    | { type: 'SET_STOCK'; payload: string }
    | { type: 'SET_PO'; payload: string }
    | { type: 'NEW_SERVICE'; payload: NewJobServiceState }
    | { type: 'EDIT_SERVICE'; payload: NewJobServiceState }
    | { type: 'REMOVE_SERVICE'; payload: number }
    | { type: 'SET_EDIT_ID'; payload: number }
    | { type: 'SET_SERVICE'; payload: NewJobServiceState }
    | { type: 'RESET_SERVICE' }
    | { type: 'RESET_SERVICES' }
    | { type: 'RESET_JOB' };

const formatCurrency = (price: string, qty: number, currency = 'USD'): string => {
    const total = (parseFloat(price) || 0) * (qty || 0);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(total);
};

const reducer = (state: NewJobState, action: NewJobAction): NewJobState => {
    switch (action.type) {
        case 'SET_LOCATION':
            return { ...state, location: action.payload };
        case 'SET_SUPPLIER':
            return { ...state, supplier: action.payload };
        case 'SET_VIN':
            return { ...state, vin: action.payload };
        case 'SET_COLOR':
            return { ...state, color: action.payload };
        case 'SET_ODO':
            return { ...state, odometer: action.payload };
        case 'SET_STOCK':
            return { ...state, stockRO: action.payload };
        case 'SET_PO':
            return { ...state, poNumber: action.payload };
        case 'NEW_SERVICE': {
            const next = [...state.services, action.payload].sort((a, b) => a.id - b.id);
            return { ...state, services: next };
        }
        case 'EDIT_SERVICE': {
            const next = state.services
                .filter((s) => s.id !== action.payload.id)
                .concat(action.payload)
                .sort((a, b) => a.id - b.id);
            return { ...state, services: next };
        }
        case 'REMOVE_SERVICE':
            return {
                ...state,
                services: state.services.filter((s) => s.id !== action.payload),
            };
        case 'SET_EDIT_ID':
            return { ...state, editID: action.payload };
        case 'SET_SERVICE': {
            const svc = { ...action.payload };
            const qty = Math.max(svc.parts.length, 1);
            svc.totalDP = formatCurrency(svc.price, qty, svc.currency);
            return { ...state, service: svc };
        }
        case 'RESET_SERVICE':
            return { ...state, service: INITIAL_SERVICE_STATE };
        case 'RESET_SERVICES':
            return { ...state, services: [] };
        case 'RESET_JOB':
            return { ...INITIAL_STATE };
        default:
            return state;
    }
};

export interface NewJobContextValue {
    state: NewJobState;
    DISPATCH: (action: NewJobAction) => void;
}

const NewJobContext = createContext<NewJobContextValue | null>(null);

export const NewJobProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;
    useEffect(() => {
        if (!loggedIn) dispatch({ type: 'RESET_JOB' });
    }, [loggedIn]);
    return (
        <NewJobContext.Provider value={{ state, DISPATCH: dispatch }}>
            {children}
        </NewJobContext.Provider>
    );
};

export const useNewJob = (): NewJobContextValue => {
    const ctx = useContext(NewJobContext);
    if (!ctx) throw new Error('useNewJob must be used inside NewJobProvider');
    return ctx;
};
